import { useEffect, useState } from 'react';

import { loadSettings, updateSetting } from 'bundles/coursera-toolbar/utils/settings';

/**
 * Hook for accessing and modifying developer toolbar settings
 */
export const useToolbarSettings = () => {
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const [bottomLocked, setBottomLocked] = useState<boolean>(false);

  // Constrain position to viewport bounds based on toolbar state
  const constrainPositionToViewport = (pos: { x: number; y: number }, willBeMinimized: boolean) => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Approximate toolbar dimensions based on state
    // These match the CSS min-width and height values
    const toolbarWidth = willBeMinimized ? 80 : 400; // Minimized: auto width (~80px), Normal: 400px min-width
    const toolbarHeight = willBeMinimized ? 28 : 50; // Minimized: 28px, Normal: 50px

    const constrainedX = Math.max(0, Math.min(pos.x, viewportWidth - toolbarWidth));
    const constrainedY = Math.max(0, Math.min(pos.y, viewportHeight - toolbarHeight));

    return { x: constrainedX, y: constrainedY };
  };

  // Load settings on initial render
  useEffect(() => {
    const settings = loadSettings();
    setIsMinimized(settings.isMinimized);
    setPosition(settings.position);
    setBottomLocked(settings.bottomLocked);
  }, []);

  // Update isMinimized state and persist to localStorage
  const toggleMinimized = () => {
    const newState = !isMinimized;

    // If toolbar has a custom position, ensure it stays within bounds when changing state
    if (position.x !== null && position.y !== null) {
      const constrainedPosition = constrainPositionToViewport({ x: position.x, y: position.y }, newState);

      // Only update position if it needs to be adjusted
      if (constrainedPosition.x !== position.x || constrainedPosition.y !== position.y) {
        setPosition(constrainedPosition);
        updateSetting('position', constrainedPosition);
      }
    }

    setIsMinimized(newState);
    updateSetting('isMinimized', newState);
  };

  // Update position and persist to localStorage
  const updatePosition = (newPosition: { x: number; y: number }) => {
    // Auto-detect if toolbar should be bottom-locked
    // If toolbar is within 60px of bottom, lock it to bottom
    const viewportHeight = window.innerHeight;
    const toolbarHeight = isMinimized ? 28 : 36;
    const distanceFromBottom = viewportHeight - newPosition.y - toolbarHeight;
    const shouldBottomLock = distanceFromBottom <= 60;

    // Store Y position relative to the edge it's locked to
    const storedPosition = {
      x: newPosition.x,
      y: shouldBottomLock ? distanceFromBottom : newPosition.y,
    };

    setPosition(storedPosition);
    updateSetting('position', storedPosition);

    if (shouldBottomLock !== bottomLocked) {
      setBottomLocked(shouldBottomLock);
      updateSetting('bottomLocked', shouldBottomLock);
    }
  };

  // Reset position to default (centered at bottom)
  const resetPosition = () => {
    const defaultPosition = { x: null, y: null };
    setPosition(defaultPosition);
    setBottomLocked(false);
    updateSetting('position', defaultPosition);
    updateSetting('bottomLocked', false);
  };

  return {
    isMinimized,
    toggleMinimized,
    position,
    updatePosition,
    resetPosition,
    bottomLocked,
  };
};
